import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseRefreshOptions {
  intervalMs: number;
  onRefresh: () => Promise<void>;
  enabled: boolean;
}

export interface UseRefreshResult {
  loading: boolean;

  /** Epoch millis of the next automatic refresh, null when disabled */
  nextRefreshAt: number | null;

  /** Refreshes immediately and restarts the timer */
  refresh: () => void;
}

/**
 * Calls `onRefresh` every `intervalMs` while `enabled`. Does not refresh on mount.
 */
export const useRefresh = ({ intervalMs, onRefresh, enabled }: UseRefreshOptions): UseRefreshResult => {
  const [loading, setLoading] = useState(false);
  const [nextRefreshAt, setNextRefreshAt] = useState<number | null>(null);
  const [timerResetCount, setTimerResetCount] = useState(0);

  const onRefreshRef = useRef(onRefresh);

  onRefreshRef.current = onRefresh;

  const doRefresh = useCallback(async () => {
    setLoading(true);

    try {
      await onRefreshRef.current();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setNextRefreshAt(null);
      return;
    }

    setNextRefreshAt(Date.now() + intervalMs);

    const intervalId = setInterval(() => {
      void doRefresh();
      setNextRefreshAt(Date.now() + intervalMs);
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [enabled, intervalMs, timerResetCount, doRefresh]);

  const refresh = useCallback(() => {
    void doRefresh();
    setTimerResetCount((count) => count + 1);
  }, [doRefresh]);

  return {
    loading,
    nextRefreshAt,
    refresh,
  };
};
